// Builds a role-agnostic searchable index of the site's pages.
// Role filtering is applied at query time via the caller's canAccess() gate,
// so users can only ever see results for pages they are permitted to view.
import { NAV_ITEMS, PAGES } from '../mock';

function collectText(data) {
  const parts = [];
  if (!data) return '';
  if (data.title) parts.push(data.title);
  if (data.intro) parts.push(data.intro);
  if (Array.isArray(data.paragraphs)) parts.push(...data.paragraphs);
  if (Array.isArray(data.list)) parts.push(...data.list);
  if (data.quote) parts.push(data.quote);
  if (data.table) {
    if (Array.isArray(data.table.head)) parts.push(...data.table.head);
    (data.table.rows || []).forEach((r) => parts.push(...r));
  }
  if (Array.isArray(data.docs)) parts.push(...data.docs.map((d) => d.name));
  if (Array.isArray(data.linkList)) parts.push(...data.linkList.map((l) => l.name));
  if (data.cta && data.cta.label) parts.push(data.cta.label);
  return parts.join(' ');
}

function buildNavMaps() {
  const pathByKey = {};
  const labelByKey = {};
  const walk = (node) => {
    if (node.pageKey && node.path) {
      pathByKey[node.pageKey] = node.path;
      labelByKey[node.pageKey] = node.label;
    }
    if (node.children) node.children.forEach(walk);
  };
  NAV_ITEMS.forEach(walk);
  return { pathByKey, labelByKey };
}

// Navigable destinations not represented in the static PAGES map.
const EXTRA = [
  {
    pageKey: 'members-directory',
    title: 'Members Directory',
    label: 'Members Directory',
    path: '/members/directory',
    text: 'members directory roster financial sappers search by name rank unit years served',
    kind: 'page',
  },
];

// Utility destinations with bespoke visibility rules.
const UTILITY = [
  {
    pageKey: '__support',
    title: 'Support Us',
    label: 'Support Us',
    path: '/support',
    text: 'support donate donation membership dues renew renewal merchandise cap polo mug reunion ticket payment stripe checkout',
    kind: 'action',
    always: true,
  },
  {
    pageKey: '__account',
    title: 'My Account',
    label: 'My Account',
    path: '/account',
    text: 'my account membership payment history receipts pdf renewal expiry',
    kind: 'action',
    requiresUser: true,
  },
  {
    pageKey: 'admin',
    title: 'Admin Dashboard',
    label: 'Admin Dashboard',
    path: '/admin',
    text: 'admin dashboard permissions members pages appearance theme fonts colours settings renewals reminders',
    kind: 'action',
  },
];

function buildStaticIndex() {
  const { pathByKey, labelByKey } = buildNavMaps();
  const records = [];
  Object.entries(PAGES).forEach(([key, data]) => {
    const path = pathByKey[key];
    if (!path) return;
    records.push({
      pageKey: key,
      title: data.title || labelByKey[key] || key,
      label: labelByKey[key] || data.title || key,
      path,
      text: collectText(data),
      kind: 'page',
    });
  });
  return [...records, ...EXTRA];
}

function buildDynamicIndex(customPages) {
  return (customPages || []).map((p) => ({
    pageKey: p.page_key,
    title: p.title,
    label: p.nav_label || p.title,
    path: p.path || `/p/${p.page_key}`,
    text: [p.title, p.nav_label, p.intro, p.body].filter(Boolean).join(' '),
    kind: 'custom',
  }));
}

// Full index = static pages + admin custom pages + utility destinations.
export function buildIndex(customPages) {
  return [...buildStaticIndex(), ...buildDynamicIndex(customPages), ...UTILITY];
}

// Resolve the current pageKey from a pathname (for "search this page first").
export function pageKeyForPath(records, pathname) {
  const exact = records.find((r) => r.path === pathname);
  if (exact) return exact.pageKey;
  if (pathname.startsWith('/members/directory')) return 'members-directory';
  return null;
}

// Score + snippet a single record against a lowercased query.
export function scoreRecord(record, q) {
  const title = (record.title || '').toLowerCase();
  const label = (record.label || '').toLowerCase();
  const text = (record.text || '').toLowerCase();
  let score = 0;
  if (title === q) score += 120;
  else if (title.startsWith(q)) score += 60;
  else if (title.includes(q)) score += 35;
  if (label.includes(q)) score += 20;
  const idx = text.indexOf(q);
  if (idx >= 0) score += 10;
  if (score === 0) return null;

  let snippet = '';
  if (idx >= 0 && record.text) {
    const start = Math.max(0, idx - 40);
    const end = Math.min(record.text.length, idx + q.length + 70);
    snippet =
      (start > 0 ? '…' : '') +
      record.text.slice(start, end).trim() +
      (end < record.text.length ? '…' : '');
  }
  return { ...record, score, snippet };
}
